import { useEffect } from 'react';
import { createPortal } from 'react-dom';
import { useQuery } from '@tanstack/react-query';
import { api } from '../../lib/api.js';

interface Recipient {
  id: string;
  workerId: string;
  workerName: string;
  workerPhone: string | null;
  status: 'pending' | 'sent' | 'delivered' | 'failed';
  sentAt: string | null;
  channel: string;
}

interface Broadcast {
  id: string;
  templateLabel: string | null;
  messageBody: string;
  subject: string | null;
  sentAt: string;
  sentBy: string | null;
  audienceSummary: string | null;
  channels: string[];
  recipients: Recipient[];
}

interface Props { onClose: () => void; broadcastId: string; }

export function BroadcastDetailModal({ onClose, broadcastId }: Props) {
  useEffect(() => {
    function onKey(e: KeyboardEvent) { if (e.key === 'Escape') onClose(); }
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  const { data: b } = useQuery({ queryKey: ['broadcasts', broadcastId], queryFn: () => api.get<Broadcast>(`/broadcasts/${broadcastId}`).then((r) => r.data) });

  if (!b) return null;

  return createPortal(
    <div className="mo open" onClick={onClose}>
      <div className="modal" id="broadcast-detail-body" style={{ width: 640, maxWidth: '98vw' }} onClick={(e) => e.stopPropagation()}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 14 }}>
          <div>
            <div className="modal-title">📢 Broadcast — {b.templateLabel ?? 'Custom'}</div>
            <div className="modal-sub">Sent {new Date(b.sentAt).toLocaleString()}{b.sentBy ? ` by ${b.sentBy}` : ''}</div>
          </div>
          <button className="close-btn" onClick={onClose}>×</button>
        </div>
        {b.audienceSummary ? (
          <div style={{ fontSize: 11, color: 'var(--muted)', marginBottom: 10 }}>Audience: {b.audienceSummary}</div>
        ) : null}
        {b.subject ? <div className="dp-row"><span className="dp-key">Subject</span><span className="dp-val">{b.subject}</span></div> : null}
        <div style={{ background: 'var(--card)', border: '1px solid var(--border)', borderRadius: 10, padding: 12, marginBottom: 12, fontSize: 12, whiteSpace: 'pre-wrap' }}>
          {b.messageBody}
        </div>
        <div className="sec-title" style={{ marginBottom: 8 }}>Recipients ({b.recipients.length})</div>
        <div style={{ maxHeight: 260, overflowY: 'auto' }}>
          {b.recipients.map((r) => (
            <div key={r.id} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '6px 0', borderBottom: '1px solid rgba(30,42,64,.4)', fontSize: 11 }}>
              <span style={{ flex: 1 }}>{r.workerName}</span>
              <span style={{ color: 'var(--muted)', fontSize: 10 }}>{r.workerPhone ?? ''}</span>
              <span className={`badge b-${r.status === 'sent' || r.status === 'delivered' ? 'valid' : r.status === 'failed' ? 'expired' : 'expiring'}`}>{r.status}</span>
            </div>
          ))}
        </div>
        <div className="modal-actions">
          <button className="btn btn-ghost" onClick={onClose}>Close</button>
        </div>
      </div>
    </div>,
    document.body,
  );
}
